import { useState } from 'react';
import type { AdminSubmissionRow, SubmissionStatus } from '@st/contract';
import { useAdminSubmissions, useUpdateSubmission } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import { formatDate } from '../../lib/format';
import { Badge, Card, Chip, Select, SkeletonList, useToast, type BadgeTone } from '../../ui';

const STATUSES: SubmissionStatus[] = ['new', 'contacted', 'converted', 'closed'];

const STATUS_KEY: Record<SubmissionStatus, string> = {
  new: 'submissions.statusNew',
  contacted: 'submissions.statusContacted',
  converted: 'submissions.statusConverted',
  closed: 'submissions.statusClosed',
};

const STATUS_TONE: Record<SubmissionStatus, BadgeTone> = {
  new: 'accent',
  contacted: 'info',
  converted: 'success',
  closed: 'neutral',
};

const SOURCE_KEY: Record<AdminSubmissionRow['source'], string> = {
  contact_form: 'submissions.sourceContactForm',
  assessment: 'submissions.sourceAssessment',
  other: 'submissions.sourceOther',
};

/** Poznata polja kalkulatora dobivaju prijevod; nepoznata se ispisuju kako jesu. */
const FIELD_KEY: Record<string, string> = {
  goal: 'submissions.fieldGoal',
  activity: 'submissions.fieldActivity',
  heightCm: 'submissions.fieldHeightCm',
  weightKg: 'submissions.fieldWeightKg',
  age: 'submissions.fieldAge',
  recommendation: 'submissions.fieldRecommendation',
};

export function AdminSubmissions() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const query = useAdminSubmissions();
  const update = useUpdateSubmission();

  const [filter, setFilter] = useState<SubmissionStatus | null>(null);

  return (
    <>
      <PageHeader
        eyebrow={t('shell.admin')}
        title={t('submissions.title')}
        subtitle={t('submissions.subtitle')}
      />

      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={4} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('empty.noSubmissions') }}
      >
        {(data) => {
          const rows = filter ? data.items.filter((s) => s.status === filter) : data.items;
          return (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                <Chip selected={filter === null} onClick={() => setFilter(null)}>
                  {t('submissions.filterAll')}
                </Chip>
                {STATUSES.map((s) => (
                  <Chip key={s} selected={filter === s} onClick={() => setFilter(s)}>
                    {t(STATUS_KEY[s])}
                  </Chip>
                ))}
              </div>

              {rows.length === 0 ? (
                <p className="text-[14px] text-ink-muted">{t('empty.noSubmissions')}</p>
              ) : (
                <ul className="flex flex-col gap-4">
                  {rows.map((s) => (
                    <Card as="li" key={s.id} className="flex flex-col gap-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="font-display text-[18px] font-semibold text-ink">
                            {s.name}
                          </h2>
                          <p className="text-[13px] text-ink-muted">
                            {s.email}
                            {s.phone ? ` · ${s.phone}` : ''}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge tone="neutral">{t(SOURCE_KEY[s.source])}</Badge>
                          <Badge tone={STATUS_TONE[s.status]}>{t(STATUS_KEY[s.status])}</Badge>
                        </div>
                      </div>

                      <p className="text-[12px] text-ink-muted">
                        {t('submissions.received')}: {formatDate(s.createdAt, locale)} ·{' '}
                        {s.locale.toUpperCase()}
                      </p>

                      <p className="whitespace-pre-line text-[14.5px] leading-relaxed text-ink">
                        {s.message ?? t('submissions.noMessage')}
                      </p>

                      {s.assessmentJson && Object.keys(s.assessmentJson).length > 0 && (
                        <section aria-label={t('submissions.assessment')}>
                          <h3 className="mb-2 text-[11px] uppercase tracking-[2px] text-ink-muted">
                            {t('submissions.assessment')}
                          </h3>
                          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                            {Object.entries(s.assessmentJson).map(([key, value]) => (
                              <div key={key} className="flex justify-between gap-3 py-1">
                                <dt className="text-[13px] text-ink-muted">
                                  {FIELD_KEY[key] ? t(FIELD_KEY[key]) : key}
                                </dt>
                                <dd className="text-[13px] text-ink">{String(value)}</dd>
                              </div>
                            ))}
                          </dl>
                        </section>
                      )}

                      {s.internalNote && (
                        <p className="rounded-md bg-bg-alt px-3 py-2 text-[13px] text-ink-muted">
                          {t('submissions.internalNote')}: {s.internalNote}
                        </p>
                      )}

                      <Select
                        id={`submission-status-${s.id}`}
                        label={t('submissions.changeStatus')}
                        value={s.status}
                        className="sm:max-w-xs"
                        options={STATUSES.map((v) => ({ value: v, label: t(STATUS_KEY[v]) }))}
                        onChange={(e) =>
                          update.mutate(
                            { id: s.id, status: e.target.value as SubmissionStatus },
                            {
                              onSuccess: () => toast(t('submissions.statusSaved'), 'success'),
                              onError: (error) =>
                                toast(toMutationMessage(t, error), 'danger'),
                            },
                          )
                        }
                      />
                    </Card>
                  ))}
                </ul>
              )}
            </>
          );
        }}
      </QueryBoundary>
    </>
  );
}
